import axios from 'axios';
import { API_BASE_URL, handleApiError } from './_apiBase';

// ── Egenkontroller ───────────────────────────────────────────────────────────

export type InspectionFieldType = 'heading' | 'checkbox' | 'select' | 'date' | 'number' | 'text';

export interface InspectionField {
    key: string;
    type: InspectionFieldType;
    label: string;
    required?: boolean;
    help?: string;
    expected?: boolean;
    deviation?: boolean;
    options?: string[];
    deviationValues?: string[];
    maxAgeDays?: number | null;
    min?: number | null;
    max?: number | null;
    unit?: string;
    sensor?: { ieee_address: string; parameter_key: string } | null;
    multiline?: boolean;
    /** Textfält som fylls i med namnet på den som gör kontrollen. */
    contact?: boolean;
}

export interface InspectionTemplate {
    id: number;
    name: string;
    category: string;            // grupp, fri text: "Brandskydd", "Underhåll" ...
    description: string | null;
    fields: InspectionField[];
    version: number;
    active: boolean;
    notify_done_contact_id: number | null;
    notify_done_name: string | null;
    /** Grupp i stället för en person: alla i gruppen får beskedet. */
    notify_done_group_id: number | null;
    notify_done_group_name: string | null;
    schedule_count?: number;
}

export interface InspectionSchedule {
    id: number;
    template_id: number;
    template_name: string | null;
    group: string;
    name: string;
    zone_id: number | null;
    zone_name: string | null;
    /** Valda zoner, områden och enskilda blandat. Ett område står för alla zoner under det. */
    zone_ids: number[];
    zone_names: string[];
    /** Antal platser när områdena vecklats ut. Två eller fler = rond, en kontroll per plats. */
    place_count: number;
    is_round: boolean;
    interval_unit: 'day' | 'week' | 'month' | 'quarter' | 'year';
    interval_count: number;
    start_date: string;
    next_due_date: string;
    remind_days_before: number;
    grace_days: number;
    responsible_contact_id: number | null;
    responsible_name: string | null;
    /** Grupp som ansvarig i stället för en person: alla i gruppen får länken. */
    responsible_group_id: number | null;
    responsible_group_name: string | null;
    escalation_contact_id: number | null;
    escalation_name: string | null;
    /** Andra kontakt som grupp i stället för en person. */
    escalation_group_id: number | null;
    escalation_group_name: string | null;
    escalation_on_deviation: boolean;
    active: boolean;
    open_count: number;
}

export interface Inspection {
    id: number;
    schedule_id: number | null;
    schedule_name: string | null;
    template_id: number;
    template_name: string | null;
    group: string | null;
    template_version: number;
    zone_id: number | null;
    zone_name: string | null;
    /** Rond: kontrollen är en av flera platser vid samma tillfälle. */
    round_id: number | null;
    round_token: string | null;
    round_total: number | null;
    round_done: number | null;
    token: string;
    due_date: string;
    status: 'pending' | 'done' | 'overdue' | 'skipped' | 'action';
    /** Påbörjad: någon har tagit kontrollen eller sparat ett utkast medan den är öppen. */
    has_draft: boolean;
    /** Vem som påbörjat kontrollen (via länken eller ett utkast). */
    started_by_contact_id: number | null;
    started_by_name: string | null;
    started_at: string | null;
    /** Schemat har en grupp som ansvarig: den som påbörjar äger kontrollen tills den är klar. */
    group_owned: boolean;
    responsible_group_name: string | null;
    has_deviation: boolean;
    /** Utfört, inget behov: gjord utan att något behövde göras. */
    no_need: boolean;
    deviation_note: string | null;
    action_note: string | null;
    action_due_date: string | null;
    action_done_note: string | null;
    action_done_at: string | null;
    action_done_by_name: string | null;
    note: string | null;
    completed_at: string | null;
    completed_by_name: string | null;
    escalation_sent_at: string | null;
    created_at: string | null;
}

export interface InspectionDetail extends Inspection {
    fields: InspectionField[];
    answers: Record<string, any>;
    prefill: Record<string, { value: number; timestamp: string; device_name: string }>;
    deviations?: { key: string; label: string; reason: string }[];
    /** Bara via länken: kontakten som öppnat sidan (?c= i länken) och om någon annan håller på. */
    viewer?: { id: number; name: string } | null;
    locked?: boolean;
}

export interface InspectionSummary {
    pending: number;
    overdue: number;
    actions: number;
    actions_overdue: number;
    deviations_30d: number;
    next_due: string | null;
}

const base = `${API_BASE_URL}/inspections`;

export const fetchInspectionSummary = async (): Promise<InspectionSummary> => {
    try { return (await axios.get(`${base}/summary`)).data; }
    catch (e) { return handleApiError(e, 'Hämta sammanfattning'); }
};

export const fetchInspectionTemplates = async (): Promise<InspectionTemplate[]> => {
    try { return (await axios.get(`${base}/templates`)).data; }
    catch (e) { return handleApiError(e, 'Hämta mallar'); }
};

export const createInspectionTemplate = async (t: Partial<InspectionTemplate>): Promise<InspectionTemplate> => {
    try { return (await axios.post(`${base}/templates`, t)).data; }
    catch (e) { return handleApiError(e, 'Spara mall'); }
};

export const updateInspectionTemplate = async (id: number, t: Partial<InspectionTemplate>): Promise<InspectionTemplate> => {
    try { return (await axios.put(`${base}/templates/${id}`, t)).data; }
    catch (e) { return handleApiError(e, 'Spara mall'); }
};

export const deleteInspectionTemplate = async (id: number): Promise<void> => {
    try { await axios.delete(`${base}/templates/${id}`); }
    catch (e) { return handleApiError(e, 'Ta bort mall'); }
};

export const fetchInspectionSchedules = async (): Promise<InspectionSchedule[]> => {
    try { return (await axios.get(`${base}/schedules`)).data; }
    catch (e) { return handleApiError(e, 'Hämta scheman'); }
};

export const createInspectionSchedule = async (s: Partial<InspectionSchedule>): Promise<{ id: number }> => {
    try { return (await axios.post(`${base}/schedules`, s)).data; }
    catch (e) { return handleApiError(e, 'Spara schema'); }
};

export const updateInspectionSchedule = async (id: number, s: Partial<InspectionSchedule>): Promise<void> => {
    try { await axios.put(`${base}/schedules/${id}`, s); }
    catch (e) { return handleApiError(e, 'Spara schema'); }
};

export const deleteInspectionSchedule = async (id: number): Promise<void> => {
    try { await axios.delete(`${base}/schedules/${id}`); }
    catch (e) { return handleApiError(e, 'Ta bort schema'); }
};

export const runInspectionSchedulerNow = async (): Promise<void> => {
    try { await axios.post(`${base}/schedules/run-now`); }
    catch (e) { return handleApiError(e, 'Kör schemaläggaren'); }
};

export const fetchInspections = async (params: { status?: 'open' | 'actions' | 'done' | 'all'; schedule_id?: number; round_id?: number; from?: string; to?: string } = {}): Promise<Inspection[]> => {
    try { return (await axios.get(base, { params })).data; }
    catch (e) { return handleApiError(e, 'Hämta kontroller'); }
};

export const fetchInspection = async (id: number): Promise<InspectionDetail> => {
    try { return (await axios.get(`${base}/${id}`)).data; }
    catch (e) { return handleApiError(e, 'Hämta kontroll'); }
};

/** Rondscheman ger round_id och id = null: en kontroll per plats skapas, öppna listan i stället. */
export const createInspection = async (body: { template_id?: number; schedule_id?: number }): Promise<{ id: number | null; round_id?: number; existing?: boolean }> => {
    try { return (await axios.post(base, body)).data; }
    catch (e) { return handleApiError(e, 'Skapa kontroll'); }
};

export interface CompleteBody {
    answers: Record<string, any>;
    deviation_mode?: 'fixed' | 'action';
    deviation_note?: string;
    action_note?: string;
    action_due_date?: string;
    note?: string;
    completed_by_name?: string;
    /** Utfört, inget behov: sparar som gjord utan att fälten krävs. */
    no_need?: boolean;
}

/** Sparar det som är ifyllt så långt, utan att kontrollen räknas som gjord. */
export const saveInspectionDraft = async (id: number, body: { answers: Record<string, any>; note?: string }): Promise<InspectionDetail> => {
    try { return (await axios.post(`${base}/${id}/draft`, body)).data; }
    catch (e) { return handleApiError(e, 'Spara utkast'); }
};

export const completeInspection = async (id: number, body: CompleteBody): Promise<InspectionDetail> => {
    try { return (await axios.post(`${base}/${id}/complete`, body)).data; }
    catch (e) { return handleApiError(e, 'Spara kontroll'); }
};

export const finishInspectionAction = async (id: number, action_done_note: string): Promise<InspectionDetail> => {
    try { return (await axios.post(`${base}/${id}/action-done`, { action_done_note })).data; }
    catch (e) { return handleApiError(e, 'Klarmarkera åtgärd'); }
};

/** Flyttar datumet för när åtgärden ska vara klar (bara när åtgärd pågår). */
export const updateInspectionActionDue = async (id: number, action_due_date: string): Promise<InspectionDetail> => {
    try { return (await axios.post(`${base}/${id}/action-due`, { action_due_date })).data; }
    catch (e) { return handleApiError(e, 'Ändra datum för åtgärd'); }
};

export const skipInspection = async (id: number, reason: string): Promise<void> => {
    try { await axios.post(`${base}/${id}/skip`, { reason }); }
    catch (e) { return handleApiError(e, 'Hoppa över kontroll'); }
};

/** CSV-export för en period, som fil. */
export const downloadInspectionCsv = async (from: string, to: string): Promise<Blob> => {
    try { return (await axios.get(`${base}/export.csv`, { params: { from, to }, responseType: 'blob' })).data; }
    catch (e) { return handleApiError(e, 'Hämta exporten'); }
};

// ── Årshjul ──────────────────────────────────────────────────────────────────

export type YearItemStatus = 'done' | 'no_need' | 'deviation' | 'pending' | 'overdue' | 'action' | 'action_overdue' | 'skipped' | 'planned';

export interface YearItem {
    date: string;                 // YYYY-MM-DD
    status: YearItemStatus;
    inspection_id: number | null; // null = planerad, finns inte som kontroll ännu
    has_deviation: boolean;
    /** Rond: pricken står för alla platser den dagen. Klick öppnar rondens lista. */
    round_id?: number | null;
    round_total?: number;
    round_done?: number;
}

export interface YearRow {
    key: string;
    schedule_id: number | null;   // null = kontroller gjorda utan schema, en rad per mall
    template_id: number;
    name: string;
    group: string;
    template_name: string;
    zone_name: string | null;
    responsible_name: string | null;
    interval_unit: 'day' | 'week' | 'month' | 'quarter' | 'year' | null;
    interval_count: number | null;
    active: boolean;
    items: YearItem[];
}

export interface YearWheel { year: number; today: string; rows: YearRow[] }

/** Årshjulet: årets tillfällen per schema, gjorda och kommande. */
export const fetchInspectionYear = async (year: number): Promise<YearWheel> => {
    try { return (await axios.get(`${base}/year`, { params: { year } })).data; }
    catch (e) { return handleApiError(e, 'Hämta årshjulet'); }
};
